import { useCallback, useEffect } from 'react';
import {
  addDoc,
  collection,
  deleteField,
  documentId,
  doc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { CollectionReference, DocumentData, DocumentReference, QueryDocumentSnapshot } from 'firebase/firestore';
import { toast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase';
import {
  buildAppsationsGitHubProxyUrl,
  describeAppsationsGitHubError,
  parseAppsationsGitHubJson,
} from '@/lib/appsationsGitHubProxy';
import { parseGitHubRepoIdentity } from '@/pages/developer/githubUtils';
import type { CodeDownloadLog, Commit, Expense, HostingDeployment, JournalEntry, RepoFile, TimeEntry } from '@/pages/developer/types';
import {
  CODE_PREVIEW_FILE_TYPES,
  MAX_REPO_FILE_SIZE,
  crc32,
  decodeBase64,
  encodeText,
  sanitizeContent,
} from '@/pages/developer/snapshotSanitizer';

const BUILD_GIT_SHA = (import.meta.env.VITE_GIT_SHA ?? '').trim();
const BUILD_TIME_UTC = (import.meta.env.VITE_BUILD_TIME ?? '').trim();

type RepoSnapshotParams = {
  actorId: string;
  canViewRepoSnapshot: boolean;
  commits: Commit[];
  expenses: Expense[];
  developerUnlocked: boolean;
  isAdminUser: boolean;
  journalEntries: JournalEntry[];
  ratePerCommit: number;
  repoOwner: string;
  repoName: string;
  repoFiles: RepoFile[];
  repoFilesStorage: 'doc' | 'subcollection';
  repoFilesUpdatedAt: string | null;
  repoFilesHydratedAt: string | null;
  firestoreDocRef: DocumentReference<DocumentData> | null;
  repoSnapshotCurrentDocRef: DocumentReference<DocumentData> | null;
  repoSnapshotFilesCollectionRef: CollectionReference<DocumentData> | null;
  firestoreCodeDownloadsCollectionRef: CollectionReference<DocumentData> | null;
  firestoreDeploymentsCollectionRef: CollectionReference<DocumentData> | null;
  timeEntries: TimeEntry[];
  retainerPaid: number;
  recordGitHubSyncError: (action: 'check-new' | 'reload-all' | 'repo-snapshot' | 'other', message: string) => void;
  setRepoFiles: (files: RepoFile[]) => void;
  setRepoFilesUpdatedAt: (value: string | null) => void;
  setRepoFilesStorage: (value: 'doc' | 'subcollection') => void;
  setRepoFileCount: (value: number | null) => void;
  setRepoFilesHydratedAt: (value: string | null) => void;
  setRepoFilesLoading: (value: boolean) => void;
  setRepoFilesError: (value: string | null) => void;
  setSelectedRepoFile: (value: RepoFile | null | ((current: RepoFile | null) => RepoFile | null)) => void;
  setRepoSnapshotCommitSha: (value: string | null) => void;
  setRepoSnapshotVersion: (value: string | null) => void;
  setRepoSnapshotGeneratingCommitSha: (value: string | null) => void;
  setCodeDownloadLogs: (value: CodeDownloadLog[]) => void;
  setCodeDownloadLogsLoading: (value: boolean) => void;
  setCodeDownloadLogsError: (value: string | null) => void;
  setHostingDeployments: (value: HostingDeployment[]) => void;
  setHostingDeploymentsLoading: (value: boolean) => void;
  setHostingDeploymentsError: (value: string | null) => void;
};

type RepoSnapshotReturn = {
  buildRepoSnapshotZipName: (payload: { version: string }) => string;
  fetchRepoSnapshot: (target?: string | null | { commitSha?: string | null; buildTimeUtc?: string | null }) => Promise<void>;
  logCodeDownload: (payload: { reason: string; version: string; commitSha: string | null }) => Promise<void>;
  deleteCurrentSnapshotFilesFromFirestore: () => Promise<void>;
};

const shouldIncludeFile = (path: string) => {
  const lower = path.toLowerCase();
  return CODE_PREVIEW_FILE_TYPES.some((ext) => lower.endsWith(ext));
};

const buildRepoSnapshotVersion = ({ sha }: { sha: string | null }) => {
  const fullSha = (sha ?? 'unknown').trim() || 'unknown';
  const shortSha = fullSha.slice(0, 7);
  return `V1.0.${shortSha}`;
};

const slugifyForFilename = (value: string) => {
  return value
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '');
};

const isFirestoreSnapshotTooLargeError = (error: unknown) => {
  const anyError = error as { code?: unknown; message?: unknown } | null;
  const code = typeof anyError?.code === 'string' ? anyError.code : '';
  const message = typeof anyError?.message === 'string' ? anyError.message : '';
  return (
    code === 'resource-exhausted' ||
    code === 'invalid-argument' ||
    /too large/i.test(message) ||
    /maximum.*(bytes|size)/i.test(message) ||
    /exceed/i.test(message)
  );
};

export const useDeveloperRepoSnapshot = (params: RepoSnapshotParams): RepoSnapshotReturn => {
  const deleteCurrentSnapshotFilesFromFirestore = useCallback(async () => {
    if (!db) return;
    if (!params.repoSnapshotFilesCollectionRef) return;

    let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
    let hasMore = true;

    while (hasMore) {
      const pageQuery = cursor
        ? query(params.repoSnapshotFilesCollectionRef, orderBy(documentId()), startAfter(cursor), limit(500))
        : query(params.repoSnapshotFilesCollectionRef, orderBy(documentId()), limit(500));
      const page = await getDocs(pageQuery);
      if (page.empty) break;

      const batch = writeBatch(db);
      page.docs.forEach((docSnap) => batch.delete(docSnap.ref));
      await batch.commit();

      cursor = page.docs[page.docs.length - 1] ?? null;
      hasMore = page.docs.length === 500;
    }
  }, [params.repoSnapshotFilesCollectionRef]);

  const persistSnapshotFilesToFirestore = useCallback(
    async (payload: {
      files: RepoFile[];
      updatedAt: string;
      commitSha: string | null;
      version: string;
    }) => {
      if (!db || !params.firestoreDocRef || !params.repoSnapshotCurrentDocRef || !params.repoSnapshotFilesCollectionRef) {
        throw new Error('Firestore is not configured for snapshot persistence.');
      }

      await deleteCurrentSnapshotFilesFromFirestore();

      const makeId = (path: string) => {
        const safe = encodeURIComponent(path).slice(0, 180);
        const hash = crc32(encodeText(path)).toString(16);
        return `${hash}-${safe}`;
      };

      for (let i = 0; i < payload.files.length; i += 400) {
        const chunk = payload.files.slice(i, i + 400);
        const batch = writeBatch(db);
        chunk.forEach((file) => {
          const fileRef = doc(params.repoSnapshotFilesCollectionRef, makeId(file.path));
          batch.set(
            fileRef,
            {
              path: file.path,
              content: file.content,
              size: file.size,
              updatedAt: file.updatedAt,
            },
            { merge: true }
          );
        });
        await batch.commit();
      }

      await setDoc(
        params.repoSnapshotCurrentDocRef,
        {
          updatedAt: payload.updatedAt,
          commitSha: payload.commitSha ?? null,
          version: payload.version,
          fileCount: payload.files.length,
        },
        { merge: true }
      );

      await setDoc(
        params.firestoreDocRef,
        {
          repoFiles: deleteField(),
          repoFilesUpdatedAt: payload.updatedAt,
          repoSnapshotCommitSha: payload.commitSha,
          repoSnapshotVersion: payload.version,
          repoFilesStorage: 'subcollection',
          repoFileCount: payload.files.length,
        },
        { merge: true }
      );

      params.setRepoFilesStorage('subcollection');
      params.setRepoFileCount(payload.files.length);
    },
    [deleteCurrentSnapshotFilesFromFirestore, params]
  );

  useEffect(() => {
    if (params.repoFilesStorage !== 'subcollection') return;
    if (!params.repoSnapshotFilesCollectionRef) return;
    if (!params.repoFilesUpdatedAt) return;
    if (!params.developerUnlocked && !params.isAdminUser) return;
    if (params.repoFilesHydratedAt === params.repoFilesUpdatedAt) return;

    let cancelled = false;
    params.setRepoFilesLoading(true);
    params.setRepoFilesError(null);

    (async () => {
      try {
        const nextFiles: RepoFile[] = [];
        let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
        let hasMore = true;

        while (hasMore) {
          const pageQuery = cursor
            ? query(params.repoSnapshotFilesCollectionRef, orderBy('path'), startAfter(cursor), limit(500))
            : query(params.repoSnapshotFilesCollectionRef, orderBy('path'), limit(500));
          const page = await getDocs(pageQuery);
          if (page.empty) break;

          page.docs.forEach((docSnap) => {
            const data = docSnap.data() as Partial<RepoFile>;
            nextFiles.push({
              path: typeof data.path === 'string' ? data.path : docSnap.id,
              content: typeof data.content === 'string' ? data.content : '',
              size: typeof data.size === 'number' ? data.size : 0,
              updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : new Date().toISOString(),
            });
          });

          cursor = page.docs[page.docs.length - 1] ?? null;
          hasMore = page.docs.length === 500;
        }

        if (cancelled) return;
        params.setRepoFiles(nextFiles);
        params.setSelectedRepoFile((current) => {
          if (!current) return nextFiles[0] ?? null;
          return nextFiles.find((file) => file.path === current.path) ?? nextFiles[0] ?? null;
        });
        params.setRepoFilesHydratedAt(params.repoFilesUpdatedAt);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Unable to load snapshot files from Firestore.';
        params.setRepoFilesError(message);
      } finally {
        if (!cancelled) params.setRepoFilesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    params.developerUnlocked,
    params.isAdminUser,
    params.repoFilesHydratedAt,
    params.repoFilesStorage,
    params.repoFilesUpdatedAt,
    params.repoSnapshotFilesCollectionRef,
    params.setRepoFiles,
    params.setRepoFilesError,
    params.setRepoFilesHydratedAt,
    params.setRepoFilesLoading,
    params.setSelectedRepoFile,
  ]);

  const buildRepoSnapshotZipName = useCallback(
    ({ version }: { version: string }) => {
      const base = params.repoName || 'repository';
      return `${base}-sanitized-${slugifyForFilename(version)}.zip`;
    },
    [params.repoName]
  );

  const logCodeDownload = useCallback(
    async (payload: { reason: string; version: string; commitSha: string | null }) => {
      if (!params.firestoreDocRef) return;

      const downloadsRef = collection(params.firestoreDocRef, 'codeDownloads');
      await addDoc(downloadsRef, {
        downloadedAt: Timestamp.now(),
        reason: payload.reason,
        version: payload.version,
        commitSha: payload.commitSha ?? null,
        repoOwner: params.repoOwner || null,
        repoName: params.repoName || null,
        repoFilesUpdatedAt: params.repoFilesUpdatedAt ?? null,
        fileCount: params.repoFiles.length,
        actorId: params.actorId,
        isAdminUser: params.isAdminUser,
        developerUnlocked: params.developerUnlocked,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
    },
    [
      params.actorId,
      params.developerUnlocked,
      params.firestoreDocRef,
      params.isAdminUser,
      params.repoFiles.length,
      params.repoFilesUpdatedAt,
      params.repoName,
      params.repoOwner,
    ]
  );

  const loadCodeDownloadLogs = useCallback(async () => {
    if (!params.firestoreCodeDownloadsCollectionRef) return [];
    const snapshot = await getDocs(
      query(params.firestoreCodeDownloadsCollectionRef, orderBy('downloadedAt', 'desc'), limit(50))
    );
    if (snapshot.empty) return [];

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data() as {
        downloadedAt?: Timestamp;
        reason?: string;
        version?: string;
        commitSha?: string | null;
        actorId?: string | null;
        fileCount?: number | null;
      };

      const downloadedAt =
        data.downloadedAt instanceof Timestamp
          ? data.downloadedAt.toDate().toISOString()
          : new Date().toISOString();

      return {
        id: docSnap.id,
        downloadedAt,
        reason: data.reason ?? '—',
        version: data.version ?? '—',
        commitSha: typeof data.commitSha === 'string' ? data.commitSha : null,
        actorId: typeof data.actorId === 'string' ? data.actorId : null,
        fileCount: typeof data.fileCount === 'number' ? data.fileCount : null,
      } satisfies CodeDownloadLog;
    });
  }, [params.firestoreCodeDownloadsCollectionRef]);

  useEffect(() => {
    if (!params.canViewRepoSnapshot) return;
    if (!params.firestoreDeploymentsCollectionRef) return;

    params.setHostingDeploymentsLoading(true);
    params.setHostingDeploymentsError(null);

    const unsubscribe = onSnapshot(
      query(params.firestoreDeploymentsCollectionRef, orderBy('firstSeenAt', 'desc'), limit(50)),
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as {
            commitSha?: string;
            buildTimeUtc?: string | null;
            firstSeenAt?: Timestamp;
            hostingOrigin?: string | null;
            recordedByActorId?: string | null;
          };

          const firstSeenAt =
            data.firstSeenAt instanceof Timestamp
              ? data.firstSeenAt.toDate().toISOString()
              : new Date().toISOString();

          return {
            id: docSnap.id,
            commitSha: typeof data.commitSha === 'string' ? data.commitSha : 'unknown',
            buildTimeUtc: typeof data.buildTimeUtc === 'string' ? data.buildTimeUtc : null,
            firstSeenAt,
            hostingOrigin: typeof data.hostingOrigin === 'string' ? data.hostingOrigin : null,
            recordedByActorId: typeof data.recordedByActorId === 'string' ? data.recordedByActorId : null,
          } satisfies HostingDeployment;
        });

        params.setHostingDeployments(next);
        params.setHostingDeploymentsLoading(false);
      },
      (error) => {
        params.setHostingDeploymentsError(error instanceof Error ? error.message : 'Unable to load deployments.');
        params.setHostingDeploymentsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
    params.canViewRepoSnapshot,
    params.firestoreDeploymentsCollectionRef,
    params.setHostingDeployments,
    params.setHostingDeploymentsError,
    params.setHostingDeploymentsLoading,
  ]);

  useEffect(() => {
    if (!params.canViewRepoSnapshot) return;
    if (!params.firestoreDeploymentsCollectionRef) return;
    if (!BUILD_GIT_SHA) return;

    const buildTimeUtc = BUILD_TIME_UTC || null;
    const docId = `${BUILD_GIT_SHA}-${buildTimeUtc ?? 'unknown'}`;
    const deploymentRef = doc(params.firestoreDeploymentsCollectionRef, docId);

    let cancelled = false;
    (async () => {
      try {
        const existing = await getDocFromServer(deploymentRef);
        if (cancelled) return;
        if (existing.exists()) return;

        const hostingOrigin = typeof window !== 'undefined' ? window.location.origin : null;
        await setDoc(deploymentRef, {
          commitSha: BUILD_GIT_SHA,
          buildTimeUtc,
          firstSeenAt: Timestamp.now(),
          hostingOrigin,
          recordedByActorId: params.actorId,
        });
      } catch {
        // Non-fatal
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params.actorId, params.canViewRepoSnapshot, params.firestoreDeploymentsCollectionRef]);

  useEffect(() => {
    if (!params.developerUnlocked && !params.isAdminUser) return;
    if (!params.firestoreCodeDownloadsCollectionRef) return;

    let cancelled = false;
    params.setCodeDownloadLogsLoading(true);
    params.setCodeDownloadLogsError(null);

    (async () => {
      try {
        const logs = await loadCodeDownloadLogs();
        if (cancelled) return;
        params.setCodeDownloadLogs(logs);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Unable to load download history.';
        params.setCodeDownloadLogsError(message);
      } finally {
        if (!cancelled) {
          params.setCodeDownloadLogsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loadCodeDownloadLogs,
    params.developerUnlocked,
    params.firestoreCodeDownloadsCollectionRef,
    params.isAdminUser,
    params.setCodeDownloadLogs,
    params.setCodeDownloadLogsError,
    params.setCodeDownloadLogsLoading,
  ]);

  const fetchRepoSnapshot = useCallback(
    async (target?: string | null | { commitSha?: string | null; buildTimeUtc?: string | null }) => {
      const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
      if (!owner || !repo) {
        toast({ title: 'Repository required', description: 'Enter the GitHub owner and repository name.' });
        return;
      }

      const explicitTargetCommitSha =
        typeof target === 'string'
          ? target.trim() || null
          : typeof target === 'object' && target
            ? (target.commitSha ?? null)
            : null;
      const deployedCommitSha = BUILD_GIT_SHA && BUILD_GIT_SHA !== 'unknown' ? BUILD_GIT_SHA : null;
      const requestedCommitSha = explicitTargetCommitSha || deployedCommitSha;
      params.setRepoSnapshotGeneratingCommitSha(requestedCommitSha);

      params.setRepoFilesLoading(true);
      params.setRepoFilesError(null);

      try {
        const repoResponse = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'repo', owner, repo }));
        if (!repoResponse.ok) {
          const detail = await describeAppsationsGitHubError(repoResponse);
          throw new Error(detail ?? `Unable to load repo metadata (HTTP ${repoResponse.status}).`);
        }
        const repoData = await parseAppsationsGitHubJson<{ default_branch?: string }>(repoResponse, 'GitHub repository');
        const defaultBranch = repoData?.default_branch ?? 'main';

        let treeRef = defaultBranch;
        let snapshotCommitSha: string | null = null;

        if (requestedCommitSha) {
          const commitResponse = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'commit', owner, repo, sha: requestedCommitSha }));

          if (!commitResponse.ok) {
            const detail = await describeAppsationsGitHubError(commitResponse);
            throw new Error(detail ?? `Unable to resolve commit ${requestedCommitSha} (HTTP ${commitResponse.status}).`);
          }

          const commitData = await parseAppsationsGitHubJson<{ sha?: string; commit?: { tree?: { sha?: string } } }>(
            commitResponse,
            'GitHub commit'
          );
          snapshotCommitSha = typeof commitData?.sha === 'string' ? commitData.sha : requestedCommitSha;
          const treeSha = typeof commitData?.commit?.tree?.sha === 'string' ? commitData.commit.tree.sha : null;
          if (!treeSha) {
            throw new Error('Unable to resolve commit tree SHA from GitHub response.');
          }
          treeRef = treeSha;
        } else {
          try {
            const latestCommitResponse = await fetch(
              buildAppsationsGitHubProxyUrl({ mode: 'commit', owner, repo, sha: defaultBranch })
            );
            if (latestCommitResponse.ok) {
              const latestData = await parseAppsationsGitHubJson<{ sha?: string }>(latestCommitResponse, 'GitHub commit');
              snapshotCommitSha = typeof latestData?.sha === 'string' ? latestData.sha : null;
            }
          } catch {
            // Non-fatal
          }
        }

        const treeResponse = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'tree', owner, repo, sha: treeRef }));
        if (!treeResponse.ok) {
          const detail = await describeAppsationsGitHubError(treeResponse);
          throw new Error(detail ?? `Unable to load repo tree (HTTP ${treeResponse.status}).`);
        }
        const treeData = await parseAppsationsGitHubJson<{ tree?: Array<{ path?: string; type?: string; sha?: string; size?: number }> }>(
          treeResponse,
          'GitHub tree'
        );
        const treeItems = Array.isArray(treeData?.tree) ? treeData.tree : [];

        const snapshot: RepoFile[] = [];

        for (const item of treeItems) {
          if (item?.type !== 'blob') continue;
          if (!item.path || !shouldIncludeFile(item.path)) continue;
          if (typeof item.size === 'number' && item.size > MAX_REPO_FILE_SIZE) continue;

          const blobResponse = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'blob', owner, repo, sha: String(item.sha ?? '') }));
          if (!blobResponse.ok) {
            continue;
          }
          const blobData = await parseAppsationsGitHubJson<{ content?: string }>(blobResponse, 'GitHub blob');
          const decoded = decodeBase64(blobData?.content ?? '');
          const { content: sanitizedContent } = sanitizeContent(decoded);
          snapshot.push({
            path: item.path,
            content: sanitizedContent,
            size: typeof item.size === 'number' ? item.size : sanitizedContent.length,
            updatedAt: new Date().toISOString(),
          });
        }

        const nextUpdatedAt = new Date().toISOString();

        const nextVersion = buildRepoSnapshotVersion({ sha: snapshotCommitSha });
        params.setRepoFiles(snapshot);
        params.setRepoFilesUpdatedAt(nextUpdatedAt);
        params.setSelectedRepoFile(snapshot[0] ?? null);
        params.setRepoSnapshotCommitSha(snapshotCommitSha);
        params.setRepoSnapshotVersion(nextVersion);
        params.setRepoFileCount(snapshot.length);
        params.setRepoFilesStorage('doc');
        params.setRepoFilesHydratedAt(nextUpdatedAt);

        if (!params.firestoreDocRef) {
          toast({ title: 'Snapshot saved locally', description: 'Connect Firestore to share the sanitized code snapshot.' });
          return;
        }

        try {
          await updateDoc(params.firestoreDocRef, {
            repoFiles: snapshot,
            repoFilesUpdatedAt: nextUpdatedAt,
            repoSnapshotCommitSha: snapshotCommitSha,
            repoSnapshotVersion: nextVersion,
            repoFilesStorage: 'doc',
            repoFileCount: snapshot.length,
          });
          params.setRepoFilesStorage('doc');
        } catch (error) {
          if (isFirestoreSnapshotTooLargeError(error)) {
            await persistSnapshotFilesToFirestore({
              files: snapshot,
              updatedAt: nextUpdatedAt,
              commitSha: snapshotCommitSha,
              version: nextVersion,
            });
          } else {
            try {
              await setDoc(
                params.firestoreDocRef,
                {
                  repoFiles: snapshot,
                  repoFilesUpdatedAt: nextUpdatedAt,
                  repoSnapshotCommitSha: snapshotCommitSha,
                  repoSnapshotVersion: nextVersion,
                  repoFilesStorage: 'doc',
                  repoFileCount: snapshot.length,
                  commits: params.commits,
                  timeEntries: params.timeEntries,
                  expenses: params.expenses,
                  retainerPaid: params.retainerPaid,
                  ratePerCommit: params.ratePerCommit,
                  repoOwner: params.repoOwner,
                  repoName: params.repoName,
                  journalEntries: params.journalEntries,
                },
                { merge: true }
              );
              params.setRepoFilesStorage('doc');
            } catch (secondaryError) {
              if (isFirestoreSnapshotTooLargeError(secondaryError)) {
                await persistSnapshotFilesToFirestore({
                  files: snapshot,
                  updatedAt: nextUpdatedAt,
                  commitSha: snapshotCommitSha,
                  version: nextVersion,
                });
              } else {
                throw secondaryError;
              }
            }
          }
        }

        toast({ title: 'Code snapshot updated', description: `Stored ${snapshot.length} sanitized files.` });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load the repo snapshot.';
        params.setRepoFilesError(message);
        params.recordGitHubSyncError('repo-snapshot', message);
        toast({ title: 'Snapshot failed', description: message });
      } finally {
        params.setRepoFilesLoading(false);
        params.setRepoSnapshotGeneratingCommitSha(null);
      }
    },
    [params, persistSnapshotFilesToFirestore]
  );

  return {
    buildRepoSnapshotZipName,
    deleteCurrentSnapshotFilesFromFirestore,
    fetchRepoSnapshot,
    logCodeDownload,
  };
};
